// --- Team Stats Dashboard ---
import { useMemo } from 'react';
import { Users, TrendingUp, User, ArrowRight, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import type { Player, Team } from '../types';

interface TeamStatsProps {
  teams: Team[];
  players: Player[];
  onViewTeam?: (team: Team) => void;
}

interface TeamStatData {
  team: Team;
  playerCount: number;
  maleCount: number;
  femaleCount: number;
  rightSide: number;
  leftSide: number;
  bothSide: number;
  totalSpent: number;
  avgSpendPerPlayer: number;
  budgetUtilization: number;
}

const TeamStats: React.FC<TeamStatsProps> = ({ teams, players, onViewTeam }) => {
  // Calculate stats for each team
  const teamStats: TeamStatData[] = useMemo(() => {
    return teams.map(team => {
      const teamPlayers = players.filter(p => p.teamId === team.id);
      const maleCount = teamPlayers.filter(p => 
        p.details?.player_gender?.toLowerCase() === 'male'
      ).length;
      const femaleCount = teamPlayers.filter(p => 
        p.details?.player_gender?.toLowerCase() === 'female'
      ).length;
      const rightSide = teamPlayers.filter(p => 
        p.details?.playing_side?.toLowerCase() === 'right'
      ).length;
      const leftSide = teamPlayers.filter(p => 
        p.details?.playing_side?.toLowerCase() === 'left'
      ).length;
      const bothSide = teamPlayers.filter(p => 
        p.details?.playing_side?.toLowerCase() === 'both'
      ).length;
      const totalSpent = teamPlayers.reduce((sum, p) => sum + p.soldPrice, 0);
      const avgSpendPerPlayer = teamPlayers.length > 0 ? totalSpent / teamPlayers.length : 0;
      const budgetUtilization = team.totalBudget > 0 ? (totalSpent / team.totalBudget) * 100 : 0;

      return {
        team,
        playerCount: teamPlayers.length,
        maleCount,
        femaleCount,
        rightSide,
        leftSide,
        bothSide,
        totalSpent,
        avgSpendPerPlayer,
        budgetUtilization
      };
    }).sort((a, b) => b.playerCount - a.playerCount);
  }, [teams, players]);

  // Overall stats
  const overallStats = useMemo(() => {
    const soldPlayers = players.filter(p => p.status === 'sold');
    const unsoldPlayers = players.filter(p => p.status === 'unsold');
    const totalSpent = soldPlayers.reduce((sum, p) => sum + p.soldPrice, 0);
    const avgPrice = soldPlayers.length > 0 ? totalSpent / soldPlayers.length : 0;
    const totalBudget = teams.reduce((sum, t) => sum + t.totalBudget, 0);
    const remainingBudget = teams.reduce((sum, t) => sum + t.currentBalance, 0);

    return {
      totalPlayers: players.length,
      soldCount: soldPlayers.length,
      unsoldCount: unsoldPlayers.length,
      totalSpent,
      avgPrice,
      totalBudget,
      remainingBudget,
      utilizationPercent: totalBudget > 0 ? ((totalBudget - remainingBudget) / totalBudget) * 100 : 0
    };
  }, [players, teams]);

  return (
    <div className="space-y-6">
      {/* Overall Summary */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6" /> Overall Auction Stats
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <p className="text-white/70 text-sm">Total Players</p>
            <p className="text-3xl font-bold">{overallStats.totalPlayers}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <p className="text-white/70 text-sm">Sold / Unsold</p>
            <p className="text-3xl font-bold">
              <span className="text-green-300">{overallStats.soldCount}</span>
              <span className="text-white/50 mx-1">/</span>
              <span className="text-orange-300">{overallStats.unsoldCount}</span>
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <p className="text-white/70 text-sm">Total Spent</p>
            <p className="text-3xl font-bold">{formatCurrency(overallStats.totalSpent)}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <p className="text-white/70 text-sm">Avg. Price</p>
            <p className="text-3xl font-bold">{formatCurrency(Math.round(overallStats.avgPrice))}</p>
          </div>
        </div>
        {/* Budget Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span>Budget Utilization</span>
            <span>{overallStats.utilizationPercent.toFixed(1)}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-400 rounded-full transition-all"
              style={{ width: `${Math.min(overallStats.utilizationPercent, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-white/60 mt-1">
            <span>Spent: {formatCurrency(overallStats.totalSpent)}</span>
            <span>Remaining: {formatCurrency(overallStats.remainingBudget)}</span>
          </div>
        </div>
      </div>

      {/* Team-wise Stats Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" /> Team-wise Statistics
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Team</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Players</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Gender (M/F)</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Side (R/L/B)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Spent</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Avg/Player</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Utilization</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {teamStats.map(stat => (
                <tr key={stat.team.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-semibold text-slate-800">{stat.team.name}</p>
                      <p className="text-xs text-slate-500">{stat.team.ownerName}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => onViewTeam?.(stat.team)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium hover:bg-indigo-200 transition-colors"
                    >
                      <User className="w-3 h-3" /> {stat.playerCount}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-blue-600 font-medium">{stat.maleCount}</span>
                    <span className="text-slate-300 mx-1">/</span>
                    <span className="text-pink-600 font-medium">{stat.femaleCount}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <span className="flex items-center gap-0.5 text-slate-600" title="Right">
                        <ArrowRight className="w-3 h-3" />{stat.rightSide}
                      </span>
                      <span className="flex items-center gap-0.5 text-slate-600" title="Left">
                        <ArrowLeft className="w-3 h-3" />{stat.leftSide}
                      </span>
                      <span className="flex items-center gap-0.5 text-slate-600" title="Both">
                        <ArrowLeftRight className="w-3 h-3" />{stat.bothSide}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {formatCurrency(stat.totalSpent)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600">
                    {formatCurrency(Math.round(stat.avgSpendPerPlayer))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={stat.team.currentBalance < 500 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                      {formatCurrency(stat.team.currentBalance)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            stat.budgetUtilization > 80 ? 'bg-red-500' : 
                            stat.budgetUtilization > 50 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(stat.budgetUtilization, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 w-10 text-right">
                        {stat.budgetUtilization.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default TeamStats;

